// Usage reports through Google Analytics, with the phone app's event names
// (services/analytics/analytics_event.dart). Never names, emails, phone
// numbers, invoice text or exact amounts: sizes go through amountBand.
// Off when the person turns "Share usage" off in Settings, and in
// development builds unless VITE_TELEMETRY_IN_DEV=true.
import { type Analytics, getAnalytics, isSupported, logEvent, setAnalyticsCollectionEnabled, setUserId, setUserProperties } from 'firebase/analytics';
import { firebaseApp } from '@/data/firebase';
import { readPreference, writePreference } from '@/data/preferences';

type Params = Record<string, string | number | boolean>;

/**
 * Every event the web app sends, with its parameters: the phone app's
 * catalogue (AnalyticsEvent in services/analytics/analytics_event.dart),
 * so both apps report under the same names. Add new events to both.
 */
export interface Events {
  // Setup and account.
  business_setup_finished: { skipped: boolean; currency: string; has_logo: boolean; has_bank_details: boolean; charges_tax: boolean };
  welcome_choice: { choice: 'start_free' | 'sign_in' };
  business_switched: { business_count: number };
  business_merged: { business_count: number };
  business_deleted: { business_count: number };
  sign_up: { method: string };
  login: { method: string };
  sign_in_failed: { method: string; reason: string };
  sign_out: Record<string, never>;
  account_deleted: { was_guest: boolean };
  // Invoices.
  invoice_created: { currency: string; item_count: number; has_client: boolean };
  invoice_edited: { was_draft: boolean };
  invoice_previewed: { is_draft: boolean };
  invoice_sent: { channel: string; format: string; first_send: boolean; template: string; amount_band: string; currency: string };
  invoice_saved: { format: string; from: string };
  public_link_created: Record<string, never>;
  template_chosen: { template: string; font: string; is_pro_template: boolean };
  payment_recorded: { method: string; paid_in_full: boolean; currency: string };
  payment_removed: Record<string, never>;
  reminder_sent: { overdue: boolean };
  invoice_duplicated: Record<string, never>;
  invoice_deleted: { was_draft: boolean; from: string };
  invoice_restored: Record<string, never>;
  invoices_filtered: { filter: string };
  search_used: { screen: 'invoices' | 'clients' | 'palette' };
  // Saved items ("My items").
  saved_item_saved: { is_new: boolean };
  saved_item_used: Record<string, never>;
  saved_item_deleted: Record<string, never>;
  // Clients.
  client_added: { source: string; from_contacts: boolean };
  client_updated: Record<string, never>;
  client_deleted: Record<string, never>;
  client_contacted: { channel: string };
  // Pro.
  paywall_viewed: { is_guest: boolean };
  subscribe_tapped: { period: string; result: string };
  // Settings and look.
  design_default_changed: { setting: string };
  setting_changed: { setting: string };
  // Web only: the search palette and keyboard shortcuts.
  palette_opened: { via: 'keyboard' | 'button' };
  palette_result_opened: { section: string };
  shortcut_used: { action: 'new_invoice' | 'search' };
  // App health.
  app_blocked: { reason: string };
  write_refused: Record<string, never>;
}

const collectInThisBuild = import.meta.env.PROD || import.meta.env.VITE_TELEMETRY_IN_DEV === 'true';
let analytics: Analytics | null = null;

export const isUsageSharingOn = () => readPreference('share_usage') !== 'off';

export async function startAnalytics() {
  if (!collectInThisBuild || !(await isSupported().catch(() => false))) return;
  analytics = getAnalytics(firebaseApp());
  setAnalyticsCollectionEnabled(analytics, isUsageSharingOn());
}

export function setUsageSharing(on: boolean) {
  writePreference('share_usage', on ? 'on' : 'off');
  if (analytics) setAnalyticsCollectionEnabled(analytics, on);
}

export function track<K extends keyof Events>(name: K, ...params: Events[K] extends Record<string, never> ? [] : [Events[K]]) {
  send(name, (params[0] ?? {}) as Params);
}

function send(name: string, params: Params) {
  if (analytics && isUsageSharingOn()) logEvent(analytics, name, params);
  else if (import.meta.env.DEV) console.debug('[analytics]', name, params);
}

/**
 * A page's address as a pattern for reports: ids (UUIDs, from newId) become
 * `:id`, so "/invoices/3f2a…/edit" is "/invoices/:id/edit".
 */
export function pagePattern(pathname: string): string {
  const path = pathname
    .split('/')
    .map((part) => (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(part) ? ':id' : part))
    .join('/')
    .replace(/\/+$/, '');
  return path || '/';
}

let lastPage: string | null = null;

/** Pages by their route pattern ("/invoices/:id"), never with ids. The same page twice in a row counts once. */
export function trackPage(pattern: string) {
  if (pattern === lastPage) return;
  lastPage = pattern;
  send('screen_view', { firebase_screen: pattern, firebase_screen_class: pattern });
}

/**
 * Who is using the app, for slicing reports, as the phone app does: the
 * pseudonymous Firebase uid and facts like plan = pro. Never names or emails.
 */
export function identify(uid: string | null, properties: Record<string, string | null>) {
  if (!analytics) return;
  setUserId(analytics, uid);
  setUserProperties(analytics, properties);
}

/** A rough size for an amount, so reports never carry what anyone was paid. */
export function amountBand(minor: number): string {
  const major = Math.trunc(Math.abs(minor) / 100);
  if (major < 100) return 'under_100';
  if (major < 1000) return '100_1k';
  if (major < 10000) return '1k_10k';
  if (major < 100000) return '10k_100k';
  if (major < 1000000) return '100k_1m';
  return 'over_1m';
}
